/* Product card image carousel (Claude, 2026-09-16)
   - hover (mouse only): entering a card swaps straight to image 2, the way the old theme
     hover-image worked, and the arrows fade in so you can keep going from there. Leaving the
     card returns to image 1.
   - arrows: one delegated click listener for the whole page. On a mouse (fine pointer) the
     track is `overflow-x: clip` so it can't swallow the wheel, which means it can't be
     scrolled either - slides move by transform. Touch keeps native scroll-snap swiping.
   - lazy slides: the browser only auto-loads a lazy image when it scrolls into the viewport.
     Neither path does that - a transformed track never "scrolls", and a clipped slide isn't
     treated as visible - so every slide past the first is woken by hand: on hover (mouse) or
     on first touch (finger), plus the next one as soon as a card is on screen. Without this a
     swipe or an arrow click lands on an image that hasn't downloaded: a blank white card. */
(function () {
  var FINE = '(hover: hover) and (pointer: fine)';
  var hovered = null;

  function isFine() { return window.matchMedia(FINE).matches; }

  function wake(card, count) {
    if (!count) {
      if (card.dataset.cicWoke) return;   // whole card already woken
      card.dataset.cicWoke = '1';
    }
    var slides = card.querySelectorAll('.cic__slide');
    /* Six slides are rendered for desktop; CSS hides 5 and 6 below 768px, so never wake them
       there - setting loading="eager" would fetch an image the shopper can't even reach. */
    var maxSlide = isFine() ? slides.length : Math.min(slides.length, 4);
    for (var i = 1; i < maxSlide && (!count || i <= count); i++) {
      var img = slides[i].querySelector('img');
      if (!img) continue;
      if (img.loading === 'lazy') img.loading = 'eager';
      /* downloaded is not the same as paintable - decode it now, or the first frame after an
         instant swap can still come up blank while the browser decodes. */
      if (img.decode) { try { img.decode().catch(function () {}); } catch (e) {} }
    }
  }

  /* `instant` skips the slide animation - the hover swap and the reset on leave should read as
     a straight image swap (the way the old hover-image did, and the way nude-project does it).
     Arrow clicks keep the slide, which is what makes them feel like paging through. */
  function goTo(track, i, instant) {
    var last = track.children.length - 1;
    i = Math.max(0, Math.min(last, i));
    track.dataset.cicIndex = i;
    if (instant) {
      track.style.transition = 'none';
      track.style.transform = 'translateX(' + (-i * 100) + '%)';
      void track.offsetWidth;            // flush, so the next move animates again
      track.style.transition = '';
      return;
    }
    track.style.transform = 'translateX(' + (-i * 100) + '%)';
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.cic__arrow');
    if (!btn) return;
    e.preventDefault();          // cards are wrapped in <a> - never navigate
    e.stopPropagation();
    var card = btn.parentElement;
    var track = card.querySelector('.cic__track');
    if (!track) return;
    wake(card);                  // safety net if hover somehow didn't fire
    var dir = btn.getAttribute('data-cic-dir') === 'next' ? 1 : -1;

    if (isFine()) {
      goTo(track, (parseInt(track.dataset.cicIndex, 10) || 0) + dir);
    } else {
      track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    }
  }, true);

  /* hovering or touching a card means the images may be about to be needed */
  function onIntent(e) {
    var card = e.target.closest && e.target.closest('.cic');
    if (!card) return;
    wake(card);
    if (!isFine() || card === hovered) return;
    hovered = card;
    var track = card.querySelector('.cic__track');
    /* first hover shows image 2; if the shopper already arrowed further, leave them there */
    if (!track || track.children.length < 2 || (parseInt(track.dataset.cicIndex, 10) || 0)) return;
    var next = track.children[1].querySelector('img');
    if (!next) return;
    if (next.complete && next.naturalWidth > 0) {
      goTo(track, 1, true);       // ready: instant swap
      return;
    }
    /* Not downloaded yet. Swapping now would show an empty slot - a white flash. Stay on
       image 1 and swap the moment it arrives, and only if the pointer is still on this card. */
    next.addEventListener('load', function () {
      if (hovered === card && !(parseInt(track.dataset.cicIndex, 10) || 0)) goTo(track, 1, true);
    }, { once: true });
  }
  document.addEventListener('mouseover', onIntent, { passive: true, capture: true });
  document.addEventListener('touchstart', onIntent, { passive: true, capture: true });

  /* leaving the card puts it back to image 1 */
  document.addEventListener('mouseout', function (e) {
    var card = e.target.closest && e.target.closest('.cic');
    if (!card || !isFine()) return;
    if (e.relatedTarget && card.contains(e.relatedTarget)) return;  // still inside the card
    if (card === hovered) hovered = null;
    var track = card.querySelector('.cic__track');
    if (track) goTo(track, 0, true);  // instant - no slide back
  }, { passive: true, capture: true });

  /* Keeping the hover image ready.
     The hover swap is instant, so image 2 has to be on the device before the pointer arrives -
     otherwise the card flashes white while it downloads. Waking it only when the card scrolls
     into view isn't early enough: hover a card a moment after it appears and you beat the
     download. So on a mouse, once the page is idle, walk every card and fetch its second image
     in small batches - after first paint, so it never competes with the images the shopper is
     actually looking at, and batched because firing them all at once saturates the connection
     and makes everything slower. Touch devices skip this entirely; they have no hover. */
  /* Keeping the hover image ready.
     The hover swap is instant, so image 2 must already be on the device when the pointer
     arrives - otherwise the card flashes white while it downloads. Two rules:
       1. cards the shopper can see (or is about to) get their second image fetched once the
          page is idle, in small batches - after first paint so it never competes with the
          images being looked at, batched because firing them all at once saturates the
          connection and makes everything slower;
       2. cards further down are left alone until they come within 600px of the viewport.
     That way a hover is never caught waiting, without pulling a second image for all 35
     products on a page the shopper may never scroll. Touch devices skip it: no hover. */
  function wakeNearViewport(margin) {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.cic').forEach(function (c) { wake(c, 1); });
      return;
    }
    var queue = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        queue.push(entry.target);
        io.unobserve(entry.target);
      });
      drain();
    }, { rootMargin: margin + 'px 0px' });
    var draining = false;
    function drain() {
      if (draining || !queue.length) return;
      draining = true;
      (function next() {
        var batch = queue.splice(0, 4);
        batch.forEach(function (c) { wake(c, 1); });
        if (queue.length) setTimeout(next, 120);
        else draining = false;
      })();
    }
    document.querySelectorAll('.cic').forEach(function (c) { io.observe(c); });
  }

  function whenIdle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 800);
  }

  var startPreload = function () {
    /* 600px ahead on a mouse so a hover is never caught waiting; the modest 200px on touch is
       only about a swipe landing on an undownloaded frame. */
    whenIdle(function () { wakeNearViewport(isFine() ? 600 : 200); });
  };

  if (document.readyState === 'complete') startPreload();
  else window.addEventListener('load', startPreload);
})();
